// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: whether the new assistant experience is switched on for the signed-in person (the
// rollout, docs/konvo/assistant-engagement.md). When it is not, the app behaves as before the
// feature: no corner icon or pop-ups, no starting points, no Home "Get started" card or DM
// starter cards, the rail keeps the "Ask Konvo / Search" tile and ⌘K answers in the palette.
// Tracking and Team insights don't look at this.
//
// Loaded on start, when the window returns (at most once a minute) and every 10 visible minutes; the
// last answer per person
// is remembered on the device so the right rail tile shows from the first frame.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import Window from '@app/features/window/state/Window';
import type {EngagementAccess} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

export const ACCESS_REFRESH_MS = 10 * 60 * 1000;
/** Focus within this long of the last answer keeps it (a rollout switch is not that urgent). */
export const ACCESS_FOCUS_GAP_MS = 60 * 1000;
const STORAGE_PREFIX = 'konvo:engagement-access:';

function remembered(userId: string): boolean | null {
	try {
		const raw = getProtectedLocalStorage()?.getItem(STORAGE_PREFIX + userId);
		return raw === '1' ? true : raw === '0' ? false : null;
	} catch {
		return null;
	}
}

function remember(userId: string, enabled: boolean): void {
	try {
		getProtectedLocalStorage()?.setItem(STORAGE_PREFIX + userId, enabled ? '1' : '0');
	} catch {
		// Private mode or full storage: the next start asks the server again.
	}
}

export class EngagementAccessStore {
	/** The new experience is on for the signed-in person (off until known). */
	enabled = false;
	private userId: string | null = null;
	/** Bumped on every start/reset: answers for the previous person are dropped. */
	private generation = 0;
	private stops: Array<() => void> = [];
	private loadedAt = Number.NEGATIVE_INFINITY;
	private pending: Promise<void> | null = null;

	constructor() {
		makeAutoObservable<this, 'userId' | 'generation' | 'stops' | 'loadedAt' | 'pending'>(
			this,
			{userId: false, generation: false, stops: false, loadedAt: false, pending: false},
			{autoBind: true},
		);
	}

	/** Start for this person (mounted once signed in); the returned function stops and forgets. */
	start(userId: string): () => void {
		this.reset();
		this.userId = userId;
		this.enabled = remembered(userId) ?? false;
		const onFocus = () => {
			if (Date.now() - this.loadedAt >= ACCESS_FOCUS_GAP_MS) void this.load();
		};
		window.addEventListener('focus', onFocus);
		const poll = new VisiblePoll(
			ACCESS_REFRESH_MS,
			() => {
				if (Date.now() - this.loadedAt >= ACCESS_FOCUS_GAP_MS) return this.load();
				return undefined;
			},
			false,
			ACCESS_REFRESH_MS,
		);
		poll.start();
		const stopVisibility = reaction(
			() => Window.visible,
			(visible) => {
				if (visible) onFocus();
			},
		);
		this.stops = [() => window.removeEventListener('focus', onFocus), () => poll.stop(), stopVisibility];
		void this.load();
		return () => this.reset();
	}

	/** Ask the server; a failure keeps what was known. */
	load(): Promise<void> {
		const userId = this.userId;
		if (!userId || !Window.visible) return Promise.resolve();
		if (this.pending) return this.pending;
		const request = this.fetch(userId, this.generation).finally(() => {
			if (this.pending === request) this.pending = null;
		});
		this.pending = request;
		return request;
	}

	private async fetch(userId: string, generation: number): Promise<void> {
		try {
			const response = await http.get<EngagementAccess>(Endpoints.KONVO_ENGAGEMENT_ACCESS, {mode: 'silent'});
			if (generation !== this.generation || typeof response.body?.enabled !== 'boolean') return;
			const enabled = response.body.enabled;
			runInAction(() => {
				this.enabled = enabled;
				this.loadedAt = Date.now();
			});
			remember(userId, enabled);
		} catch {
			// Offline or a blip: keep the last answer until the next focus or interval.
		}
	}

	/** Sign-out or account switch: stop and forget. */
	reset(): void {
		this.generation += 1;
		for (const stop of this.stops) stop();
		this.stops = [];
		this.userId = null;
		this.enabled = false;
		this.loadedAt = Number.NEGATIVE_INFINITY;
		this.pending = null;
	}
}

export default new EngagementAccessStore();
